import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useOutletContext, useSearchParams } from "react-router-dom"
import Filter from "pages/purchase/filter"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Pagination, Transition } from "components"
import { lang } from "config"
import { Pencil, Plus } from "components/icons/outline"
import { useResultSet } from "repositories/db-repository"
import { getBranchList } from "helpers/general-helper"

export default function PurchaseReturnList() {
  const { can, currentBranch } = useAuth()
  const { title, transition, setTransition } = useOutletContext()
  const location = useLocation()

  const [searchParams, setSearchParams] = useSearchParams()

  const page = searchParams.get("page")
  const status = searchParams.get("status")
  const receiptId = searchParams.get("receipt_id")
  const poId = searchParams.get("po_id")
  const searchParam = searchParams.get("search") ?? ""

  // Local state untuk search (debounced)
  const [search, setSearch] = useState(searchParam)
  const searchInput = useRef(null)
  const searchDebounceRef = useRef(null)

  useEffect(() => setSearch(searchParam), [searchParam])

  const branchSelection = getBranchList()
  const branchIdList = useMemo(() => {
    if (!branchSelection) return []
    return branchSelection.map((x) => x.id).filter(Boolean)
  }, [branchSelection])

  /**
   * Endpoint list return:
   * - table: purchase_returns
   * - filter: outlet_id, status, receipt_id, po_id, search (return_number / supplier snapshot / etc)
   */
  const { data: result_set, isLoading, mutate } = useResultSet(
    `${process.env.REACT_APP_INVENTORY_API_URL}`,
    `purchase-returns`,
    {
      outlet_id: currentBranch?.id, // outlet_id INT
      branch_list: JSON.stringify(branchIdList),
      page,
      record_per_page: 10,
      search: searchParam || undefined,
      status: status || undefined,
      receipt_id: receiptId || undefined,
      po_id: poId || undefined,
    }
  )

  // kalau kamu punya list_flow reuse component Filter yang sama
  // const { data: list_flow } = useResultSet(
  //   `${process.env.REACT_APP_INVENTORY_API_URL}`,
  //   `list_flow`,
  //   {}
  // )

  useEffect(() => {
    if (transition?.reverse) setTransition({ type: "slide", reverse: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (searchInput.current) searchInput.current.focus()
  }, [])

  useEffect(() => {
    if (isLoading) nProgress.start()
    else nProgress.done()
  }, [isLoading])

  const patchSearchParams = (patch, opts = {}) => {
    const next = new URLSearchParams(searchParams)

    Object.entries(patch || {}).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k)
      else next.set(k, String(v))
    })

    // reset page ketika filter berubah
    if (
      patch &&
      (Object.prototype.hasOwnProperty.call(patch, "search") ||
        Object.prototype.hasOwnProperty.call(patch, "status") ||
        Object.prototype.hasOwnProperty.call(patch, "receipt_id") ||
        Object.prototype.hasOwnProperty.call(patch, "po_id"))
    ) {
      next.delete("page")
    }

    setSearchParams(next, opts)
  }

  const updateFilter = (value) => patchSearchParams(value)
  const removeFilter = () => setSearchParams({})

  const onSearchChange = (val) => {
    setSearch(val)
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => {
      patchSearchParams({ search: val })
    }, 350)
  }

  const generatePaginationParam = () => {
    let tmp = ""
    if (status) tmp += `&status=${status}`
    if (receiptId) tmp += `&receipt_id=${receiptId}`
    if (poId) tmp += `&po_id=${poId}`
    if (searchParams.has("search")) tmp += `&search=${searchParams.get("search")}`
    return tmp
  }

  const list = useMemo(() => {
    if (Array.isArray(result_set?.data?.data)) return result_set.data?.data
    return []
  }, [result_set])

  const paginationLinks = result_set?.data?.links
  const total = result_set?.data?.total
  const from = result_set?.data?.from
  const to = result_set?.data?.to

  const statusBadge = (st) => {
    const s = String(st || "").toUpperCase()
    const base = "inline-flex items-center px-2 py-1 rounded-lg text-[11px] font-medium"
    if (s === "POSTED") return <span className={`${base} bg-green-50 text-green-700`}>{st}</span>
    if (s === "DRAFT") return <span className={`${base} bg-neutral-100 text-neutral-700`}>{st}</span>
    if (s === "CANCELLED") return <span className={`${base} bg-red-50 text-red-700`}>{st}</span>
    return <span className={`${base} bg-neutral-100 text-neutral-700`}>{st || "-"}</span>
  }

  const formatDateTime = (value) => {
    if (!value) return "-"

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value

    return new Intl.DateTimeFormat("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date)
  }

  const postJson = async (path, body = null) => {
    const url = `${process.env.REACT_APP_INVENTORY_API_URL.replace(/\/$/, "")}${path}`
    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body ? JSON.stringify(body) : null,
    })
    const json = await resp.json().catch(() => ({}))
    if (!resp.ok) {
      const msg = json?.message || "Request gagal"
      const err = json?.error ? `: ${json.error}` : ""
      throw new Error(msg + err)
    }
    return json
  }

  const [actingId, setActingId] = useState(null)

  const onPost = async (ret) => {
    if (!ret?.id) return
    if (!window.confirm(`Post Return ${ret.return_number || ""}?`)) return

    try {
      setActingId(ret.id)
      await postJson(`/purchase-returns/${ret.id}/post`)
      await mutate()
    } catch (e) {
      alert(e.message || "Gagal post return")
    } finally {
      setActingId(null)
    }
  }

  const onCancel = async (ret) => {
    if (!ret?.id) return
    const reason = window.prompt(`Cancel Return ${ret.return_number || ""}\nAlasan (optional):`, "")
    if (reason === null) return

    try {
      setActingId(ret.id)
      await postJson(`/purchase-returns/${ret.id}/cancel`, reason ? { reason } : null)
      await mutate()
    } catch (e) {
      alert(e.message || "Gagal cancel return")
    } finally {
      setActingId(null)
    }
  }

  const canPost = (ret) => String(ret?.status || "").toUpperCase() === "DRAFT"
  const canCancel = (ret) => String(ret?.status || "").toUpperCase() === "DRAFT"

  return (
    <Transition type="fade">
      <div className="pb-4 space-y-6 lg:pb-8">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center sm:space-x-2">
            <div className="relative hidden sm:block">
              <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <circle cx={10} cy={10} r={7}></circle>
                  <line x1={21} y1={21} x2={15} y2={15}></line>
                </svg>
              </div>

              <input
                ref={searchInput}
                onChange={(e) => onSearchChange(e.target.value)}
                value={search}
                type="text"
                placeholder={`${lang.search} Purchase Return`}
                autoComplete="off"
                className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
              />
            </div>

            <select
              value={status || ""}
              onChange={(e) => patchSearchParams({ status: e.target.value })}
              className="hidden sm:block py-3 px-3 text-xs transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            >
              <option value="">All Status</option>
              <option value="DRAFT">DRAFT</option>
              <option value="SUBMITTED">SUBMITTED</option>
              <option value="APPROVED">APPROVED</option>
              <option value="REJECTED">REJECTED</option>
              <option value="POSTED">POSTED</option>
              <option value="CANCELLED">CANCELLED</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            {can("return-order.create") && (
              <div>
                <Link
                  to="create"
                  onClick={() => setTransition({ type: "slide", reverse: true })}
                  state={{ back: title, from: location?.pathname, transition: { type: "slide" } }}
                  className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90"
                >
                  <Plus className="w-4 h-4" stroke={1.5} />
                  <span>{lang.create}</span>
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto border border-neutral-200 rounded-xl">
          <table className="min-w-full overflow-x-auto divide-y divide-neutral-200">
            <thead className="bg-neutral-50 rounded-t-3xl">
              <tr>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Return No</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">PO</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">GR</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Supplier</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Date</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Status</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Items</th>
                <th className="relative px-3 py-3"><span className="sr-only">{lang.action}</span></th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-neutral-200">
              {isLoading && (
                <tr className="text-center">
                  <td colSpan={8} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.loading_data}
                  </td>
                </tr>
              )}

              {!isLoading && list.length === 0 && !searchParam && (
                <tr className="text-center">
                  <td colSpan={8} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.no_data}
                  </td>
                </tr>
              )}

              {!isLoading && list.length === 0 && searchParam && (
                <tr className="text-center">
                  <td colSpan={8} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.no_result}
                  </td>
                </tr>
              )}

              {!isLoading && list.map((ret) => (
                <tr key={ret.id}>
                  <td className="px-3 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                    {ret.return_number ?? "-"}
                  </td>

                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {ret?.purchase_order?.po_number ?? ret.po_id ?? "-"}
                  </td>

                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {ret.goods_receipt.gr_number ?? ret.receipt_id ?? "-"}
                  </td>

                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {ret?.purchase_order?.supplier_name_snapshot ?? "-"}
                  </td>

                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {formatDateTime(ret.updated_at) ?? "-"}
                  </td>

                  <td className="px-3 py-4 text-xs whitespace-nowrap">
                    {statusBadge(ret.status)}
                  </td>

                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {ret.items_count ?? "-"}
                  </td>

                  <td className="px-3 py-4 text-xs font-medium text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-2">
                      {(can("return-order.update") || can("return-order.view")) && (
                        <Link
                          to={`${ret.id}/edit`}
                          state={{ back: title, from: location?.pathname, transition: "slide" }}
                          className="inline-flex items-center p-1 text-white transition rounded-full bg-neutral-800 active:hover:scale-90"
                        >
                          <Pencil className="w-6 h-6" />
                        </Link>
                      )}

                      {can("return-order.post") && canPost(ret) && (
                        <button
                          type="button"
                          disabled={actingId === ret.id}
                          onClick={() => onPost(ret)}
                          className="px-3 py-2 text-[11px] font-semibold text-white bg-green-700 rounded-lg hover:bg-green-600 disabled:opacity-60"
                        >
                          {actingId === ret.id ? "..." : "Post"}
                        </button>
                      )}

                      {can("return-order.cancel") && canCancel(ret) && (
                        <button
                          type="button"
                          disabled={actingId === ret.id}
                          onClick={() => onCancel(ret)}
                          className="px-3 py-2 text-[11px] font-semibold text-white bg-red-600 rounded-lg hover:bg-red-500 disabled:opacity-60"
                        >
                          {actingId === ret.id ? "..." : "Cancel"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          params={generatePaginationParam()}
          links={paginationLinks}
          from={from}
          to={to}
          total={total}
        />
      </div>
    </Transition>
  )
}